import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../shared/services/api.service';
import { Subscription } from 'rxjs';
import { CartService } from '../../shared/services/cart.service';
import { AuthService } from '../../shared/services/auth.service';
import { DeliveryDateService } from '../../shared/services/delivery-date.service';

@Component({
  selector: 'app-select-cart',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './select-cart.component.html',
  styleUrl: './select-cart.component.scss',
})
export class SelectCartComponent implements OnInit {
  @Input() item: any = null;
  @Input() isSelected: boolean = false;
  sub: Subscription = new Subscription();

  @Output() remove = new EventEmitter<void>();
  @Output() moveToWishlist = new EventEmitter<void>();
  @Output() selectionChange = new EventEmitter<{
    item: any;
    selected: boolean;
    deliveryOption: string;
  }>();
  isExpanded: boolean = false;

  selectedOptiondata: string = 'billing';
  dropdownOpen = false;
  selectedOption: 'Home Delivery' | 'Store Pickup' = 'Home Delivery';
  isStorePickupLocked = false;
  showMoreOptionsModal = false;
  selectedAction: 'delete' | 'wishlist' = 'delete';

  constructor(
    private apiService: ApiService,
    private cartService: CartService,
    private authService: AuthService,
    private deliveryDateService: DeliveryDateService,
  ) {}

  get isUserLoggedIn(): boolean {
    return this.authService.isLoggedIn;
  }
  ngOnInit() {
    // Add null checking for item and ensure part_image_path is an array
    if (this.item) {
      // Ensure part_image_path is always an array
      if (
        this.item.part_image_path &&
        !Array.isArray(this.item.part_image_path)
      ) {
        this.item.part_image_path = [this.item.part_image_path];
      } else if (!this.item.part_image_path) {
        this.item.part_image_path = [];
      }

      this.selectedOptiondata = this.item.quality_s || 'billing';
      // Default to Store Pickup for Scrap Part items
      if (this.item?.part_type === 'Scrap Part') {
        this.selectedOption = 'Store Pickup';
        this.isStorePickupLocked = true;
        this.dropdownOpen = false;
      } else {
        this.selectedOption = 'Home Delivery';
        this.isStorePickupLocked = false;
      }
    } else {
      // Default values when item is not available
      this.selectedOptiondata = 'billing';
      this.selectedOption = 'Home Delivery';
    }
  }
  toggle() {
    if (this.isStorePickupLocked) {
      return;
    }
    this.isExpanded = !this.isExpanded;
  }

  selectDelivery(type: string) {
    // this.deliveryType = type;
    this.isExpanded = false;
  }
  removeItem(item: any) {
    // If not logged in OR this item is marked as local, remove from localStorage only
    const isLoggedIn = this.authService.isLoggedIn;
    const isLocal = item?.is_local_storage_item === true;
    if (!isLoggedIn || isLocal) {
      const id = (item?.inventory_id || item?.part_number || item?.id)?.toString();
      if (id) {
        this.cartService.removeFromLocalStorage(id);
      }
      this.remove.emit();
      return;
    }

    const payload = {
      cart_item_id_list: [item?.inventory_id?.toString()],
    };

    // Optimistically decrease cart count immediately
    let currentCount = 0;
    this.cartService.cartItemCount$
      .subscribe((count) => (currentCount = count))
      .unsubscribe?.();
    this.cartService.updateCartCount(Math.max(currentCount - 1, 0));

    this.sub.add(
      this.apiService.removeCartData(payload).subscribe({
        next: (res: any) => {
          if (res.success) {
            this.remove.emit();
            // Refresh cart count after successful removal
            this.cartService.updateCartCountFromApi();
          } else {
            console.error('Failed to remove cart item:', res);
          }
        },
        error: (err: any) => {
          console.error('Error removing cart item:', err);
        },
        complete: () => {
          // no-op
        },
      }),
    );
  }
  moveToWishlistItem(item: any) {
    this.toggleFavorite(item);
    this.moveToWishlist.emit();
  }

  toggleDropdown() {
    if (this.isStorePickupLocked) {
      return;
    }
    this.dropdownOpen = !this.dropdownOpen;
  }

  selectOption(option: 'Home Delivery' | 'Store Pickup') {
    if (this.isStorePickupLocked && option !== 'Store Pickup') {
      this.selectedOption = 'Store Pickup';
      this.dropdownOpen = false;
      return;
    }
    this.selectedOption = option;
    this.dropdownOpen = false;
    if (this.isSelected) {
      this.selectionChange.emit({
        item: this.item,
        selected: this.isSelected,
        deliveryOption:
          option === 'Store Pickup' ? 'Store Pickup' : 'Home Delivery',
      });
    }
  }

  getIcon(option: 'Home Delivery' | 'Store Pickup') {
    return option === 'Home Delivery'
      ? 'assets/Icon _Set.png'
      : 'https://cdn.builder.io/api/v1/image/assets/TEMP/cf5854ebd36dd295aa62cbf916e76cf351d766d5'; // store icon
  }

  onCheckboxChange(event: any) {
    this.isSelected = event.target.checked;
    this.selectionChange.emit({
      item: this.item,
      selected: this.isSelected,
      deliveryOption: this.selectedOption,
    });
  }

  toggleFavorite(data: any) {
    // Block wishlist for guests
    if (!this.authService.isLoggedIn) {
      return;
    }

    const payload: any = {
      action_type: 'add_or_update',
    };

    payload['wishlist_items'] = [
      {
        part_number: data?.part_number,
        total_quantity: data?.total_quantity ? data?.total_quantity : 1,
        part_type: data?.part_type,
        price: data?.discounted_price ? data?.discounted_price : data?.price_s,
      },
    ];

    this.sub.add(
      this.apiService.addToWishlist(payload).subscribe({
        next: () => {
          // After adding to wishlist, remove from cart
          this.removeItemAfterWishlist(data);
        },
        error: (err) => {
          console.error('Error adding to wishlist:', err);
        },
        complete: () => {},
      }),
    );
  }

  removeItemAfterWishlist(item: any) {
    // If not logged in OR this item is marked as local, remove from localStorage only
    const isLoggedIn = this.authService.isLoggedIn;
    const isLocal = item?.is_local_storage_item === true;

    if (!isLoggedIn || isLocal) {
      const id = (item?.inventory_id || item?.part_number || item?.id)?.toString();
      if (id) {
        this.cartService.removeFromLocalStorage(id);
      }
      // Optimistically decrease cart count immediately
      let currentCount = 0;
      this.cartService.cartItemCount$
        .subscribe((count) => (currentCount = count))
        .unsubscribe?.();
      this.cartService.updateCartCount(Math.max(currentCount - 1, 0));
      this.remove.emit();
      return;
    }

    const payload = {
      cart_item_id_list: [item?.inventory_id?.toString()],
    };

    // Optimistically decrease cart count immediately
    let currentCount = 0;
    this.cartService.cartItemCount$
      .subscribe((count) => (currentCount = count))
      .unsubscribe?.();
    this.cartService.updateCartCount(Math.max(currentCount - 1, 0));

    this.sub.add(
      this.apiService.removeCartData(payload).subscribe({
        next: (res: any) => {
          if (res.success) {
            // Only emit remove event after cart removal succeeds
            this.remove.emit();
            // Refresh cart count after successful removal
            this.cartService.updateCartCountFromApi();
          } else {
            console.error('Failed to remove cart item:', res);
            // Revert optimistic update on failure
            this.cartService.updateCartCountFromApi();
          }
        },
        error: (err: any) => {
          console.error('Error removing cart item:', err);
          // Revert optimistic update on error
          this.cartService.updateCartCountFromApi();
        },
        complete: () => {},
      }),
    );
  }

  openMoreOptionsModal() {
    this.showMoreOptionsModal = true;
    this.selectedAction = 'delete'; // Reset to default
  }

  closeMoreOptionsModal() {
    this.showMoreOptionsModal = false;
  }

  showConfirmDeleteModal: boolean = false;
  pendingDeleteItem: any = null;

  confirmRemove(item: any): void {
    this.pendingDeleteItem = item;
    this.showConfirmDeleteModal = true;
  }

  cancelDelete(): void {
    this.showConfirmDeleteModal = false;
    this.pendingDeleteItem = null;
  }

  executeDelete(): void {
    if (this.pendingDeleteItem) {
      const itemToDelete = this.pendingDeleteItem;
      this.showConfirmDeleteModal = false;
      this.pendingDeleteItem = null;
      this.removeItem(itemToDelete);
    }
  }

  handleContinue() {
    if (this.selectedAction === 'delete') {
      this.closeMoreOptionsModal();
      this.confirmRemove(this.item);
    } else if (this.selectedAction === 'wishlist') {
      this.moveToWishlistItem(this.item);
      this.closeMoreOptionsModal();
    }
  }

  /**
   * Get formatted delivery date (3 business days from today)
   */
  getDeliveryDate(): string {
    return this.deliveryDateService.calculateDeliveryDate(3);
  }

  /**
   * Get formatted pickup date (2 business days from today)
   */
  getPickupDate(): string {
    return this.deliveryDateService.calculateDeliveryDate(2);
  }

  ngOnDestroy() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }
}
